'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface ProblemSectionProps {
  isLoading?: boolean;
}

export function ProblemSection({ isLoading = false }: ProblemSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-9 w-[60%]" />
          <div className="mx-auto max-w-[800px] space-y-6">
            <div className="space-y-2">
              <Skeleton className="mx-auto h-6 w-[30%]" />
              <Skeleton className="mx-auto h-6 w-[28%]" />
              <Skeleton className="mx-auto h-6 w-[32%]" />
            </div>
            <Skeleton className="mx-auto h-6 w-[45%]" />
            <Skeleton className="mx-auto h-64 w-full rounded-xl" />
            <Skeleton className="mx-auto h-7 w-[55%]" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-8 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          A passing build does not prove the code is correct.
        </h2>

        <div
          className="mx-auto max-w-[800px] space-y-6 text-center"
          style={{ fontFamily: theme.fonts.body }}
        >
          <p className="text-lg md:text-xl">
            The code "works."
            <br />
            The tests pass.
            <br />
            The build succeeds.
          </p>

          <p
            className="text-foreground/70 text-lg md:text-xl"
            style={{ fontStyle: 'italic' }}
          >
            The build can pass while the code gets worse.
          </p>

          <div
            className="rounded-xl p-6 text-base md:p-8 md:text-lg"
            style={{
              background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
              border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
            }}
          >
            <p className="text-foreground/80 mb-3">
              A passing build can still hide:
            </p>
            <ul className="text-foreground/70 space-y-1">
              <li>• Weakened a failing test until it passed</li>
              <li>• Marked the task complete with the build broken</li>
              <li>• Claimed output it never produced</li>
              <li>• Swallowed a real error instead of handling it</li>
              <li>• Invented an API that doesn't exist</li>
            </ul>
          </div>

          <p
            className="pt-4 text-2xl font-semibold md:text-3xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            Complete is not the same as correct.
          </p>
        </div>
      </div>
    </section>
  );
}
